import { useState, useEffect } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, addWeeks, subWeeks } from "date-fns";
import { de, enUS, pt } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, XCircle, Download, FileText, Maximize2, Minimize2, Copy, ClipboardList, StickyNote, Bell, CheckCircle2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import ShiftModal from "@/components/ShiftModal";
import AbsenceModal from "@/components/AbsenceModal";
import CopyShiftModal from "@/components/CopyShiftModal";
import { useLanguage } from "@/contexts/LanguageContext";

import WeekTimelineView from "@/components/WeekTimelineView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardPage({ onLogout }) {
  const { lang, t } = useLanguage();
  const dateLocale = lang === "pt" ? pt : lang === "en" ? enUS : de;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [absenceModalOpen, setAbsenceModalOpen] = useState(false);
  const [copyShiftModalOpen, setCopyShiftModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [viewMode, setViewMode] = useState('expanded'); // 'compact' or 'expanded'
  const [calendarView, setCalendarView] = useState('month'); // 'month' or 'week'

  // Manager items state
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", priority: "medium", due_date: "", start_time: "", end_time: "", category: "general", is_recurring: false, recurrence_type: null });
  const [noteForm, setNoteForm] = useState({ content: "", category: "general", pinned: false });
  const [reminderForm, setReminderForm] = useState({ client_name: "", reminder_type: "follow_up", note: "", due_date: "", start_time: "", end_time: "" });
  const [editingTask, setEditingTask] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editingReminder, setEditingReminder] = useState(null);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const weekDays = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const firstDayOfWeek = (monthStart.getDay() + 6) % 7; // Monday-start: Mon=0, Tue=1 ... Sun=6

  useEffect(() => {
    loadData();
  }, [currentDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const monthStr = format(currentDate, 'yyyy-MM');

      const [shiftsRes, absencesRes, workersRes, shiftTypesRes, tasksRes, notesRes, remindersRes] = await Promise.all([
        axios.get(`${API}/shifts?month=${monthStr}`, { headers }),
        axios.get(`${API}/absences?month=${monthStr}`, { headers }),
        axios.get(`${API}/workers`, { headers }),
        axios.get(`${API}/shift-types`, { headers }),
        axios.get(`${API}/tasks?include_completed=false`, { headers }),
        axios.get(`${API}/notes`, { headers }),
        axios.get(`${API}/reminders`, { headers }),
      ]);

      setShifts(shiftsRes.data);
      setAbsences(absencesRes.data);
      setWorkers(workersRes.data);
      setShiftTypes(shiftTypesRes.data);
      setTasks(tasksRes.data.tasks || []);
      setNotes(notesRes.data.notes || []);
      setReminders(remindersRes.data.reminders || []);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  const downloadPDF = async (workerId = null) => {
    try {
      const token = localStorage.getItem('token');
      const monthStr = format(currentDate, 'yyyy-MM');
      
      let url = `${API}/reports/monthly-pdf?month=${monthStr}`;
      if (workerId) {
        url += `&worker_id=${workerId}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `schichtplan_${monthStr}${workerId ? '_' + workers.find(w => w.id === workerId)?.name : ''}.pdf`;
      link.click();
      
      toast.success("PDF erfolgreich heruntergeladen!");
    } catch (error) {
      toast.error("Fehler beim Herunterladen des PDFs");
    }
  };

  const handleCopyShift = (shift) => {
    setSelectedShift(shift);
    setCopyShiftModalOpen(true);
  };

  const handleAddShift = (day) => {
    setSelectedDate(format(day, 'yyyy-MM-dd'));
    setSelectedShift(null);
    setShiftModalOpen(true);
  };

  const handleAddAbsence = (day) => {
    setSelectedDate(format(day, 'yyyy-MM-dd'));
    setSelectedAbsence(null);
    setAbsenceModalOpen(true);
  };

  const handleShiftClick = (shift, e) => {
    e.stopPropagation();
    setSelectedShift(shift);
    setSelectedDate(shift.date);
    setShiftModalOpen(true);
  };

  const handleAbsenceClick = (absence, e) => {
    e.stopPropagation();
    setSelectedAbsence(absence);
    setSelectedDate(absence.date);
    setAbsenceModalOpen(true);
  };

  // Manager item handlers
  const handleAddTask = (day) => {
    setEditingTask(null);
    setTaskForm({ title: "", description: "", priority: "medium", due_date: format(day, 'yyyy-MM-dd'), start_time: "", end_time: "", category: "general", is_recurring: false, recurrence_type: null });
    setIsTaskModalOpen(true);
  };
  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({ title: task.title, description: task.description || "", priority: task.priority, due_date: task.due_date || "", start_time: task.start_time || "", end_time: task.end_time || "", category: task.category, is_recurring: task.is_recurring, recurrence_type: task.recurrence_type });
    setIsTaskModalOpen(true);
  };

  const handleAddNote = (day) => {
    setEditingNote(null);
    setNoteForm({ content: "", category: "general", pinned: false, _calendarDate: format(day, 'yyyy-MM-dd') });
    setIsNoteModalOpen(true);
  };
  const handleEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({ content: note.content, category: note.category, pinned: note.pinned });
    setIsNoteModalOpen(true);
  };

  const handleAddReminder = (day) => {
    setEditingReminder(null);
    setReminderForm({ client_name: "", reminder_type: "follow_up", note: "", due_date: format(day, 'yyyy-MM-dd'), start_time: "", end_time: "" });
    setIsReminderModalOpen(true);
  };
  const handleEditReminder = (reminder) => {
    setEditingReminder(reminder);
    setReminderForm({ client_name: reminder.client_name, reminder_type: reminder.reminder_type, note: reminder.note || "", due_date: reminder.due_date, start_time: reminder.start_time || "", end_time: reminder.end_time || "" });
    setIsReminderModalOpen(true);
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) { toast.error(t("error")); return; }
    try {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      const payload = { ...taskForm, start_time: taskForm.start_time || null, end_time: taskForm.end_time || null };
      if (editingTask) await axios.put(`${API}/tasks/${editingTask.id}`, payload, { headers: h });
      else await axios.post(`${API}/tasks`, payload, { headers: h });
      toast.success(t("success"));
      setIsTaskModalOpen(false); setEditingTask(null); loadData();
    } catch { toast.error(t("error")); }
  };
  const handleDeleteTask = async () => {
    if (!editingTask) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tasks/${editingTask.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("success")); setIsTaskModalOpen(false); setEditingTask(null); loadData();
    } catch { toast.error(t("error")); }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    if (!noteForm.content.trim()) { toast.error(t("error")); return; }
    try {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      const { _calendarDate, ...notePayload } = noteForm;
      if (editingNote) await axios.put(`${API}/notes/${editingNote.id}`, notePayload, { headers: h });
      else await axios.post(`${API}/notes`, notePayload, { headers: h });
      toast.success(t("success")); setIsNoteModalOpen(false); setEditingNote(null); loadData();
    } catch { toast.error(t("error")); }
  };
  const handleDeleteNote = async () => {
    if (!editingNote) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/notes/${editingNote.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("success")); setIsNoteModalOpen(false); setEditingNote(null); loadData();
    } catch { toast.error(t("error")); }
  };

  const handleCreateReminder = async (e) => {
    e.preventDefault();
    if (!reminderForm.client_name.trim()) { toast.error(t("error")); return; }
    try {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      const payload = { ...reminderForm, start_time: reminderForm.start_time || null, end_time: reminderForm.end_time || null };
      if (editingReminder) await axios.put(`${API}/reminders/${editingReminder.id}`, payload, { headers: h });
      else await axios.post(`${API}/reminders`, payload, { headers: h });
      toast.success(t("success")); setIsReminderModalOpen(false); setEditingReminder(null); loadData();
    } catch { toast.error(t("error")); }
  };
  const handleDeleteReminder = async () => {
    if (!editingReminder) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/reminders/${editingReminder.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("success")); setIsReminderModalOpen(false); setEditingReminder(null); loadData();
    } catch { toast.error(t("error")); }
  };

  const getTasksForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return tasks.filter(t => t.due_date === dateStr && !t.is_completed);
  };

  const getRemindersForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return reminders.filter(r => r.due_date === dateStr && !r.is_completed);
  };

  const getShiftsForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return shifts.filter(s => s.date === dateStr);
  };

  const getAbsencesForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return absences.filter(a => a.date === dateStr);
  };

  const getShiftTypeColor = (typeId) => {
    const type = shiftTypes.find(st => st.id === typeId);
    if (!type) return 'gray';
    return type.color;
  };

  const getShiftTime = (shift) => {
    if (shift.custom_start_time && shift.custom_end_time) {
      return `${shift.custom_start_time} - ${shift.custom_end_time}`;
    }
    const type = shiftTypes.find(st => st.id === shift.shift_type_id);
    if (!type) return '';
    return `${type.start_time} - ${type.end_time}`;
  };

  const calculateShiftHours = (shift) => {
    let startTime, endTime;
    
    if (shift.custom_start_time && shift.custom_end_time) {
      startTime = shift.custom_start_time;
      endTime = shift.custom_end_time;
    } else {
      const type = shiftTypes.find(st => st.id === shift.shift_type_id);
      if (!type || type.start_time === '--:--' || type.end_time === '--:--') return null;
      startTime = type.start_time;
      endTime = type.end_time;
    }
    
    if (startTime === '--:--' || endTime === '--:--') return null;
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let hours = endHour - startHour;
    let minutes = endMin - startMin;
    
    if (minutes < 0) {
      hours -= 1;
      minutes += 60;
    }
    
    if (hours < 0) hours += 24; // Handle overnight shifts
    
    const totalHours = hours + (minutes / 60);
    return totalHours % 1 === 0 ? `${totalHours}h` : `${totalHours.toFixed(1)}h`;
  };

  const getWorkerName = (workerId) => {
    const worker = workers.find(w => w.id === workerId);
    return worker ? worker.name : 'Unbekannt';
  };

  const colorClasses = {
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    pink: 'bg-pink-50 text-pink-700 border-pink-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200'
  };

  const absenceColorClasses = {
    'Urlaub': 'bg-green-100 text-green-800 border-green-300',
    'Krank': 'bg-red-100 text-red-800 border-red-300',
    'Schule': 'bg-purple-100 text-purple-800 border-purple-300'
  };

  if (loading) {
    return (
      <Layout onLogout={onLogout}>
        <div className="flex items-center justify-center h-full">
          <p className="text-slate-600">Laden...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6 h-full flex flex-col" data-testid="dashboard-page">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("calendar_title")}
            </h1>
            <p className="text-slate-600 text-sm mt-1">{t("app_tagline")}</p>
          </div>
          
          <div className="flex gap-2">
            <div className="flex bg-slate-100 rounded-md p-0.5 mr-2">
              <button onClick={() => setCalendarView('month')}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${calendarView === 'month' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="view-month"
              >{t("landing_monthly").replace(/lich$|ly$|l$/,'')}</button>
              <button onClick={() => setCalendarView('week')}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1 ${calendarView === 'week' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="view-week"
              ><CalendarClock className="w-3.5 h-3.5" />{t("week")}</button>
            </div>
            <Button
              variant={viewMode === 'expanded' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('expanded')}
              data-testid="view-mode-expanded"
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              {t("expanded_view")}
            </Button>
            <Button
              variant={viewMode === 'compact' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('compact')}
              data-testid="view-mode-compact"
            >
              <Minimize2 className="w-4 h-4 mr-2" />
              {t("compact_view")}
            </Button>
          
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800 ml-2" data-testid="download-pdf-button">
                  <Download className="w-4 h-4 mr-2" />
                  {t("download_pdf")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Bericht herunterladen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => downloadPDF()} data-testid="download-all-pdf">
                  <FileText className="w-4 h-4 mr-2" />
                  Alle Mitarbeiter
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Nach Mitarbeiter</DropdownMenuLabel>
                {workers.map(worker => (
                  <DropdownMenuItem 
                    key={worker.id} 
                    onClick={() => downloadPDF(worker.id)}
                    data-testid={`download-worker-pdf-${worker.id}`}
                  >
                    {worker.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {calendarView === 'month' ? (
        <>
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-3 border-b border-slate-200">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrevMonth}
              data-testid="prev-month-button"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="current-month">
              {format(currentDate, 'MMMM yyyy', { locale: dateLocale })}
            </h2>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNextMonth}
              data-testid="next-month-button"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-auto flex flex-col">
            <div className="grid grid-cols-7 border-b border-slate-200 sticky top-0 bg-white z-10">
              {weekDays.map(day => (
                <div key={day} className="p-2 text-center text-xs font-medium text-slate-700 border-r border-slate-100 last:border-r-0">
                  {day}
                </div>
              ))}
            </div>

            <div className="flex-1 grid grid-rows-[repeat(var(--rows),minmax(90px,1fr))]" style={{ '--rows': Math.ceil((daysInMonth.length + firstDayOfWeek) / 7) }}>
              {Array.from({ length: Math.ceil((daysInMonth.length + firstDayOfWeek) / 7) }).map((_, weekIndex) => (
                <div key={weekIndex} className="grid grid-cols-7 border-b border-slate-100 last:border-b-0 min-h-[90px]">
                  {Array.from({ length: 7 }).map((_, dayIndex) => {
                    const cellIndex = weekIndex * 7 + dayIndex;
                    
                    if (cellIndex < firstDayOfWeek) {
                      return (
                        <div key={`empty-${cellIndex}`} className="border-r border-slate-100 bg-slate-50/50 last:border-r-0" />
                      );
                    }
                    
                    const dayNumber = cellIndex - firstDayOfWeek;
                    if (dayNumber >= daysInMonth.length) {
                      return <div key={`empty-end-${cellIndex}`} className="border-r border-slate-100 last:border-r-0" />;
                    }
                    
                    const day = daysInMonth[dayNumber];
                    const dayShifts = getShiftsForDay(day);
                    const dayAbsences = getAbsencesForDay(day);
                    const dayTasks = getTasksForDay(day);
                    const dayReminders = getRemindersForDay(day);
                    const totalItems = dayShifts.length + dayAbsences.length + dayTasks.length + dayReminders.length;
                    const isCompact = viewMode === 'compact';
                    // In expanded mode, show max 4 items then overflow count
                    const maxVisible = isCompact ? 6 : 4;
                    const hasOverflow = totalItems > maxVisible && !isCompact;
                    
                    const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                    
                    return (
                      <div
                        key={day.toString()}
                        className={`border-r border-slate-100 last:border-r-0 p-1 hover:bg-slate-50 transition-colors group relative flex flex-col min-w-0 ${isToday ? 'bg-red-50/40' : ''}`}
                        data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                      >
                        <div className="flex items-center justify-between mb-0.5 flex-shrink-0">
                          <span className={`text-[10px] font-medium ${isToday ? 'bg-red-400 text-white w-5 h-5 rounded-full flex items-center justify-center' : 'text-slate-700'}`}>{format(day, 'd')}</span>
                          {totalItems > 0 && (
                            <span className="text-[8px] text-slate-400 font-medium">{totalItems}</span>
                          )}
                        </div>
                        
                        <div className="space-y-0.5 flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent">
                          {dayShifts.map(shift => {
                            const color = getShiftTypeColor(shift.shift_type_id);
                            const timeRange = getShiftTime(shift);
                            const shiftHours = calculateShiftHours(shift);
                            const hasCustomTime = !!(shift.custom_start_time && shift.custom_end_time);
                            
                            if (viewMode === 'compact') {
                              return (
                                <div
                                  key={shift.id}
                                  className={`rounded h-4 border cursor-pointer hover:brightness-95 transition-all ${colorClasses[color] || colorClasses.gray} ${hasCustomTime ? 'ring-1 ring-blue-400' : ''}`}
                                  onClick={(e) => handleShiftClick(shift, e)}
                                  data-testid={`shift-card-${shift.id}`}
                                  title={`${getWorkerName(shift.worker_id)} - ${timeRange}${shiftHours ? ` (${shiftHours})` : ''}`}
                                />
                              );
                            }
                            
                            return (
                              <div
                                key={shift.id}
                                className={`rounded px-1 py-0.5 text-[9px] font-medium border shadow-sm cursor-pointer hover:brightness-95 transition-all group/shift relative min-w-0 ${colorClasses[color] || colorClasses.gray} ${hasCustomTime ? 'ring-1 ring-blue-400' : ''}`}
                                onClick={(e) => handleShiftClick(shift, e)}
                                data-testid={`shift-card-${shift.id}`}
                              >
                                <div className="font-semibold leading-tight truncate">{getWorkerName(shift.worker_id)}</div>
                                <div className="text-[8px] opacity-75 leading-tight truncate" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{timeRange}</div>
                                {shiftHours && (
                                  <div className="text-[8px] font-bold opacity-90 leading-tight" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{shiftHours}</div>
                                )}
                                <button
                                  className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-slate-900 text-white rounded-full opacity-0 group-hover/shift:opacity-100 transition-opacity flex items-center justify-center z-10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyShift(shift);
                                  }}
                                  title="Kopieren"
                                >
                                  <Copy className="w-2 h-2" />
                                </button>
                              </div>
                            );
                          })}

                          {dayAbsences.map(absence => (
                            <div
                              key={absence.id}
                              className={`rounded px-1 py-0.5 text-[9px] font-medium border-2 border-dashed cursor-pointer hover:brightness-95 transition-all min-w-0 ${absenceColorClasses[absence.type] || 'bg-gray-100 text-gray-800'}`}
                              onClick={(e) => handleAbsenceClick(absence, e)}
                              data-testid={`absence-card-${absence.id}`}
                            >
                              <div className="flex items-center gap-0.5 min-w-0">
                                <XCircle className="w-2 h-2 flex-shrink-0" />
                                <span className="font-semibold leading-tight truncate">{getWorkerName(absence.worker_id)}</span>
                              </div>
                              <div className="text-[8px] opacity-75 leading-tight truncate">{absence.type}</div>
                            </div>
                          ))}

                          {/* Tasks for this day */}
                          {dayTasks.map(task => (
                            isCompact ? (
                              <div key={`task-${task.id}`} className="rounded h-4 bg-amber-50 border border-amber-200 cursor-pointer hover:brightness-90" title={task.title} onClick={(e) => { e.stopPropagation(); handleEditTask(task); }} />
                            ) : (
                              <div
                                key={`task-${task.id}`}
                                className="rounded px-1 py-0.5 text-[9px] font-medium bg-amber-50 text-amber-700 border border-amber-200 min-w-0 cursor-pointer hover:bg-amber-100 transition-colors"
                                title={task.title}
                                onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                              >
                                <div className="flex items-center gap-0.5 min-w-0">
                                  <ClipboardList className="w-2 h-2 flex-shrink-0" />
                                  <span className="leading-tight truncate">{task.start_time ? `${task.start_time} ` : ''}{task.title}</span>
                                </div>
                              </div>
                            )
                          ))}

                          {/* Reminders for this day */}
                          {dayReminders.map(reminder => (
                            isCompact ? (
                              <div key={`rem-${reminder.id}`} className="rounded h-4 bg-cyan-50 border border-cyan-200 cursor-pointer hover:brightness-90" title={`${reminder.client_name} - ${reminder.reminder_type}`} onClick={(e) => { e.stopPropagation(); handleEditReminder(reminder); }} />
                            ) : (
                              <div
                                key={`rem-${reminder.id}`}
                                className="rounded px-1 py-0.5 text-[9px] font-medium bg-cyan-50 text-cyan-700 border border-cyan-200 min-w-0 cursor-pointer hover:bg-cyan-100 transition-colors"
                                title={`${reminder.client_name} - ${reminder.reminder_type}`}
                                onClick={(e) => { e.stopPropagation(); handleEditReminder(reminder); }}
                              >
                                <div className="flex items-center gap-0.5 min-w-0">
                                  <Bell className="w-2 h-2 flex-shrink-0" />
                                  <span className="leading-tight truncate">{reminder.start_time ? `${reminder.start_time} ` : ''}{reminder.client_name}</span>
                                </div>
                              </div>
                            )
                          ))}
                        </div>

                        <div className="absolute bottom-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="w-4 h-4 rounded bg-slate-900 text-white flex items-center justify-center hover:bg-slate-800">
                                <Plus className="w-3 h-3" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuLabel>{t("shift_planning")}</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => handleAddShift(day)} data-testid={`add-shift-${format(day, 'yyyy-MM-dd')}`}>
                                {t("add_shift")}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddAbsence(day)} data-testid={`add-absence-${format(day, 'yyyy-MM-dd')}`}>
                                {t("add_absence")}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>{t("manager")}</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => handleAddTask(day)} data-testid={`add-task-${format(day, 'yyyy-MM-dd')}`}>
                                <ClipboardList className="w-3.5 h-3.5 mr-2 text-amber-500" />
                                {t("add_task")}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddNote(day)} data-testid={`add-note-${format(day, 'yyyy-MM-dd')}`}>
                                <StickyNote className="w-3.5 h-3.5 mr-2 text-blue-500" />
                                {t("add_note")}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddReminder(day)} data-testid={`add-reminder-${format(day, 'yyyy-MM-dd')}`}>
                                <Bell className="w-3.5 h-3.5 mr-2 text-cyan-500" />
                                {t("add_reminder")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-4 text-xs text-slate-600 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-50 border-2 border-blue-400" />
            <span>{t("custom_time")}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-red-100 border-2 border-red-300 border-dashed" />
            <span>{t("absence")}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-amber-50 border border-amber-200" />
            <span>{t("task")}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-cyan-50 border border-cyan-200" />
            <span>{t("reminder")}</span>
          </div>
        </div>
        </>
        ) : (
        /* ========== WEEK TIMELINE VIEW ========== */
        <WeekTimelineView
          currentDate={currentDate}
          shifts={shifts}
          absences={absences}
          workers={workers}
          shiftTypes={shiftTypes}
          tasks={tasks}
          reminders={reminders}
          dateLocale={dateLocale}
          t={t}
          onPrevWeek={() => setCurrentDate(subWeeks(currentDate, 1))}
          onNextWeek={() => setCurrentDate(addWeeks(currentDate, 1))}
          onShiftClick={(shift) => { setSelectedShift(shift); setSelectedDate(shift.date); setShiftModalOpen(true); }}
          onAddShift={(day) => { setSelectedDate(format(day, 'yyyy-MM-dd')); setSelectedShift(null); setShiftModalOpen(true); }}
          onAddAbsence={(day) => { setSelectedDate(format(day, 'yyyy-MM-dd')); setSelectedAbsence(null); setAbsenceModalOpen(true); }}
          onAddTask={handleAddTask}
          onAddNote={handleAddNote}
          onAddReminder={handleAddReminder}
          onEditTask={handleEditTask}
          onEditReminder={handleEditReminder}
          onUpdateItemTime={async (itemType, itemId, startTime, endTime, newDate) => {
            try {
              const token = localStorage.getItem('token');
              const h = { Authorization: `Bearer ${token}` };
              if (itemType === 'task') {
                const payload = { start_time: startTime, end_time: endTime };
                if (newDate) payload.due_date = newDate;
                await axios.put(`${API}/tasks/${itemId}`, payload, { headers: h });
              } else if (itemType === 'shift') {
                const payload = { custom_start_time: startTime, custom_end_time: endTime };
                if (newDate) payload.date = newDate;
                await axios.put(`${API}/shifts/${itemId}`, payload, { headers: h });
              } else if (itemType === 'reminder') {
                const payload = { start_time: startTime, end_time: endTime };
                if (newDate) payload.due_date = newDate;
                await axios.put(`${API}/reminders/${itemId}`, payload, { headers: h });
              }
              loadData();
            } catch { toast.error(t("error")); }
          }}
        />
        )}
      </div>

      {shiftModalOpen && (
        <ShiftModal
          open={shiftModalOpen}
          onClose={() => {
            setShiftModalOpen(false);
            setSelectedShift(null);
            setSelectedDate(null);
          }}
          date={selectedDate}
          shift={selectedShift}
          workers={workers}
          shiftTypes={shiftTypes}
          onSuccess={loadData}
        />
      )}

      {absenceModalOpen && (
        <AbsenceModal
          open={absenceModalOpen}
          onClose={() => {
            setAbsenceModalOpen(false);
            setSelectedAbsence(null);
            setSelectedDate(null);
          }}
          date={selectedDate}
          absence={selectedAbsence}
          workers={workers}
          onSuccess={loadData}
        />
      )}

      {copyShiftModalOpen && selectedShift && (
        <CopyShiftModal
          open={copyShiftModalOpen}
          onClose={() => {
            setCopyShiftModalOpen(false);
            setSelectedShift(null);
          }}
          shift={selectedShift}
          shiftType={shiftTypes.find(st => st.id === selectedShift.shift_type_id)}
          worker={workers.find(w => w.id === selectedShift.worker_id)}
          currentMonth={currentDate}
          onSuccess={loadData}
        />
      )}

      {/* Task Modal */}
      <Dialog open={isTaskModalOpen} onOpenChange={(open) => { setIsTaskModalOpen(open); if (!open) setEditingTask(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTask ? t("edit") + " " + t("task") : t("new_task")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div>
              <Label>{t("title")} *</Label>
              <Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} placeholder={t("what_needs_done")} data-testid="cal-task-title" />
            </div>
            <div>
              <Label>{t("description")}</Label>
              <Textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} placeholder={t("details")} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("priority")}</Label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">{t("priority_high")}</SelectItem>
                    <SelectItem value="medium">{t("priority_medium")}</SelectItem>
                    <SelectItem value="low">{t("priority_low")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("category")}</Label>
                <Select value={taskForm.category} onValueChange={(v) => setTaskForm({ ...taskForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">{t("cat_general")}</SelectItem>
                    <SelectItem value="client">{t("cat_client")}</SelectItem>
                    <SelectItem value="equipment">{t("cat_equipment")}</SelectItem>
                    <SelectItem value="inventory">{t("cat_inventory")}</SelectItem>
                    <SelectItem value="staff">{t("cat_staff")}</SelectItem>
                    <SelectItem value="marketing">{t("cat_marketing")}</SelectItem>
                    <SelectItem value="finance">{t("cat_finance")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("due_date")}</Label>
              <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("start_time")}</Label>
                <Input type="time" value={taskForm.start_time || ""} onChange={(e) => setTaskForm({ ...taskForm, start_time: e.target.value })} />
              </div>
              <div>
                <Label>{t("end_time")}</Label>
                <Input type="time" value={taskForm.end_time || ""} onChange={(e) => setTaskForm({ ...taskForm, end_time: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="cal-recurring" checked={taskForm.is_recurring} onCheckedChange={(c) => setTaskForm({ ...taskForm, is_recurring: c, recurrence_type: c ? "weekly" : null })} />
              <Label htmlFor="cal-recurring">{t("recurring")}</Label>
            </div>
            {taskForm.is_recurring && (
              <Select value={taskForm.recurrence_type || "weekly"} onValueChange={(v) => setTaskForm({ ...taskForm, recurrence_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">{t("recurrence_daily")}</SelectItem>
                  <SelectItem value="weekly">{t("recurrence_weekly")}</SelectItem>
                  <SelectItem value="monthly">{t("recurrence_monthly")}</SelectItem>
                </SelectContent>
              </Select>
            )}
            <DialogFooter className="flex justify-between">
              {editingTask && <Button type="button" variant="destructive" onClick={handleDeleteTask} data-testid="cal-delete-task">{t("delete")}</Button>}
              <div className="flex gap-2 ml-auto">
                <Button type="button" variant="outline" onClick={() => { setIsTaskModalOpen(false); setEditingTask(null); }}>{t("cancel")}</Button>
                <Button type="submit" data-testid="cal-save-task">{editingTask ? t("save") : t("create")}</Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Note Modal */}
      <Dialog open={isNoteModalOpen} onOpenChange={(open) => { setIsNoteModalOpen(open); if (!open) setEditingNote(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingNote ? t("edit") + " " + t("note") : t("new_note")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateNote} className="space-y-4">
            <div>
              <Label>{t("content")} *</Label>
              <Textarea value={noteForm.content} onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })} placeholder={t("your_note")} rows={4} data-testid="cal-note-content" />
            </div>
            <div>
              <Label>{t("category")}</Label>
              <Select value={noteForm.category} onValueChange={(v) => setNoteForm({ ...noteForm, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">{t("cat_general")}</SelectItem>
                  <SelectItem value="client">{t("cat_client")}</SelectItem>
                  <SelectItem value="meeting">{t("cat_meeting")}</SelectItem>
                  <SelectItem value="idea">{t("cat_idea")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="cal-pinned" checked={noteForm.pinned} onCheckedChange={(c) => setNoteForm({ ...noteForm, pinned: c })} />
              <Label htmlFor="cal-pinned">{t("pin_note")}</Label>
            </div>
            <DialogFooter className="flex justify-between">
              {editingNote && <Button type="button" variant="destructive" onClick={handleDeleteNote} data-testid="cal-delete-note">{t("delete")}</Button>}
              <div className="flex gap-2 ml-auto">
                <Button type="button" variant="outline" onClick={() => { setIsNoteModalOpen(false); setEditingNote(null); }}>{t("cancel")}</Button>
                <Button type="submit" data-testid="cal-save-note">{editingNote ? t("save") : t("create")}</Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reminder Modal */}
      <Dialog open={isReminderModalOpen} onOpenChange={(open) => { setIsReminderModalOpen(open); if (!open) setEditingReminder(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingReminder ? t("edit") + " " + t("reminder") : t("new_reminder")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateReminder} className="space-y-4">
            <div>
              <Label>{t("client_name")} *</Label>
              <Input value={reminderForm.client_name} onChange={(e) => setReminderForm({ ...reminderForm, client_name: e.target.value })} placeholder={t("client_name_placeholder")} data-testid="cal-reminder-client" />
            </div>
            <div>
              <Label>{t("reminder_type")}</Label>
              <Select value={reminderForm.reminder_type} onValueChange={(v) => setReminderForm({ ...reminderForm, reminder_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="follow_up">{t("type_follow_up")}</SelectItem>
                  <SelectItem value="birthday">{t("type_birthday")}</SelectItem>
                  <SelectItem value="renewal">{t("type_renewal")}</SelectItem>
                  <SelectItem value="feedback">{t("type_feedback")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("date")}</Label>
              <Input type="date" value={reminderForm.due_date} onChange={(e) => setReminderForm({ ...reminderForm, due_date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("start_time")}</Label>
                <Input type="time" value={reminderForm.start_time || ""} onChange={(e) => setReminderForm({ ...reminderForm, start_time: e.target.value })} />
              </div>
              <div>
                <Label>{t("end_time")}</Label>
                <Input type="time" value={reminderForm.end_time || ""} onChange={(e) => setReminderForm({ ...reminderForm, end_time: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>{t("note")}</Label>
              <Textarea value={reminderForm.note} onChange={(e) => setReminderForm({ ...reminderForm, note: e.target.value })} placeholder={t("additional_info")} rows={2} />
            </div>
            <DialogFooter className="flex justify-between">
              {editingReminder && <Button type="button" variant="destructive" onClick={handleDeleteReminder} data-testid="cal-delete-reminder">{t("delete")}</Button>}
              <div className="flex gap-2 ml-auto">
                <Button type="button" variant="outline" onClick={() => { setIsReminderModalOpen(false); setEditingReminder(null); }}>{t("cancel")}</Button>
                <Button type="submit" data-testid="cal-save-reminder">{editingReminder ? t("save") : t("create")}</Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
